import type { FormEvent, JSX } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Heading1 } from '@/components/blocks/typography';

interface LoginScreenProps {
  email: string;
  password: string;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  onClearError?: () => void;
}

export function LoginScreen({
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  isSubmitting = false,
  errorMessage,
  onClearError,
}: LoginScreenProps): JSX.Element {
  const handleEmailChange = (value: string) => {
    if (errorMessage) onClearError?.();
    onEmailChange(value);
  };

  const handlePasswordChange = (value: string) => {
    if (errorMessage) onClearError?.();
    onPasswordChange(value);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-100 px-6 py-12">
      <div className="w-full max-w-[1100px]">
        <Card className="overflow-hidden rounded-2xl shadow-2xl border-0 ring-1 ring-black/5">
          <CardContent className="grid min-h-[650px] p-0 md:grid-cols-2">
            <form onSubmit={onSubmit} className="flex flex-col justify-center p-8 md:p-16">
              <div className="mb-12 flex flex-col items-center gap-2 text-center md:items-start md:text-left">
                {/* Mobile Brand - Visible only on small screens */}
                <div className="mb-8 flex flex-col items-center gap-3 md:hidden">
                  <img
                    src="/brand/logo-compact.png"
                    alt="Luca Milanesi"
                    className="h-10 w-auto drop-shadow-sm"
                  />
                  <span className="text-xl font-bold leading-tight text-brand-blue-950">
                    Luca Milanesi
                    <br />
                    <span className="text-sm font-medium text-brand-blue-700">Sistema Tecnico</span>
                  </span>
                </div>

                <div className="mb-2 inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-700 ring-1 ring-inset ring-blue-700/10">
                  Area Riservata
                </div>
                <Heading1 className="text-3xl font-bold tracking-tight text-neutral-950 md:text-4xl">
                  Accedi al sistema
                </Heading1>
                <p className="text-base text-neutral-500">
                  Inserisci le credenziali per continuare
                </p>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="auth-email" className="text-neutral-700">
                    Email
                  </Label>
                  <Input
                    id="auth-email"
                    type="email"
                    value={email}
                    onChange={(event) => handleEmailChange(event.target.value)}
                    autoComplete="email"
                    required
                    className="h-11 bg-neutral-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="auth-password" className="text-neutral-700">
                    Password
                  </Label>
                  <Input
                    id="auth-password"
                    type="password"
                    value={password}
                    onChange={(event) => handlePasswordChange(event.target.value)}
                    autoComplete="current-password"
                    required
                    className="h-11 bg-neutral-50"
                  />
                </div>
              </div>

              {errorMessage ? (
                <Alert variant="destructive" className="mt-6">
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}

              <div className="mt-8 space-y-4">
                <Button
                  type="submit"
                  className="h-12 w-full text-base font-medium shadow-md transition-all hover:shadow-lg active:scale-[0.98]"
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Accesso in corso...' : 'Accedi'}
                </Button>
                <p className="text-center text-xs text-neutral-400">
                  Usa le credenziali fornite dal team tecnico.
                </p>
              </div>
            </form>

            <div className="relative hidden bg-brand-blue-900 md:block">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: "url('/brand/login-cover.jpg')",
                }}
              />
              <div className="absolute inset-0 bg-blue-700/80 mix-blend-multiply" />
              <div className="relative flex h-full flex-col items-center justify-center p-10 text-white">
                <img
                  src="/brand/logo-white.png"
                  alt="Luca Milanesi"
                  className="mb-6 h-24 w-auto drop-shadow-lg"
                />
                <h2 className="text-center text-2xl font-bold leading-tight tracking-tight text-white drop-shadow-md">
                  Luca Milanesi
                  <br />
                  Sistema Tecnico
                </h2>
                <p className="mt-4 text-center text-sm font-medium uppercase tracking-[0.2em] text-blue-100/90 drop-shadow-sm">
                  Ingegneria e consulenza industriale
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
